/**
 * Closed set of application error codes (IMPLEMENTATION_PLAN.md §2.4).
 *
 * The HTTP layer maps codes to statuses; errors never carry a status themselves.
 * Only `clientSafe` messages are shown to API clients — everything else is
 * reported as the code plus a correlation id.
 */
export const ERROR_CODES = [
  "BAD_REQUEST",
  "VALIDATION_FAILED",
  "NOT_FOUND",
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "CONFLICT",
  "RATE_LIMITED",
  "SERVICE_UNAVAILABLE",
  "INTERNAL",
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export interface AppErrorOptions {
  readonly clientSafe?: boolean;
  readonly details?: Readonly<Record<string, unknown>>;
  readonly cause?: unknown;
}

export class AppError extends Error {
  readonly code: ErrorCode;
  readonly clientSafe: boolean;
  readonly details: Readonly<Record<string, unknown>> | undefined;

  constructor(code: ErrorCode, message: string, options: AppErrorOptions = {}) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = "AppError";
    this.code = code;
    this.clientSafe = options.clientSafe ?? false;
    this.details = options.details;
  }
}

export class ValidationError extends AppError {
  constructor(message: string, details?: Readonly<Record<string, unknown>>) {
    super("VALIDATION_FAILED", message, { clientSafe: true, details });
    this.name = "ValidationError";
  }
}

export class NotFoundError extends AppError {
  constructor(what: string) {
    super("NOT_FOUND", `${what} not found`, { clientSafe: true });
    this.name = "NotFoundError";
  }
}

export class UnauthenticatedError extends AppError {
  constructor(message = "Authentication required") {
    super("UNAUTHENTICATED", message, { clientSafe: true });
    this.name = "UnauthenticatedError";
  }
}

export class ForbiddenError extends AppError {
  constructor(message = "Not allowed") {
    super("FORBIDDEN", message, { clientSafe: true });
    this.name = "ForbiddenError";
  }
}

export class ConflictError extends AppError {
  constructor(message: string, details?: Readonly<Record<string, unknown>>) {
    super("CONFLICT", message, { clientSafe: true, details });
    this.name = "ConflictError";
  }
}

export class RateLimitedError extends AppError {
  readonly retryAfterSeconds: number | undefined;

  constructor(retryAfterSeconds?: number) {
    super("RATE_LIMITED", "Too many requests", { clientSafe: true });
    this.name = "RateLimitedError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export class ServiceUnavailableError extends AppError {
  constructor(message: string, cause?: unknown) {
    super("SERVICE_UNAVAILABLE", message, { clientSafe: false, cause });
    this.name = "ServiceUnavailableError";
  }
}

export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError;
}

/** Wraps anything thrown into an AppError; unknown failures become non-client-safe INTERNAL. */
export function toAppError(value: unknown): AppError {
  if (isAppError(value)) return value;
  const message = value instanceof Error ? value.message : String(value);
  return new AppError("INTERNAL", message, { cause: value });
}
